import { t } from "../i18n";
import { isFolder, type CoreApi, type TcItem } from "../tc/coreApi";
import { formatBytes, errorMessage } from "../util/format";
import { clear, el, progressBar } from "./dom";

export interface ProjectTreeOptions {
  rootId: string;
  rootName: string;
  /** Lower-case extensions that may be opened; everything else is shown but disabled. */
  accept: string[];
  /** Marks a file as the one currently shown in the viewer, for a quick visual check. */
  isLoaded?: (item: TcItem) => boolean;
  /** Called with the opened file and the id of the folder it was opened from. */
  onOpen: (item: TcItem, folderId: string) => void;
  /**
   * Lower-case extensions counted towards a "load this folder as a dataset" offer — a
   * folder containing two or more of them gets a banner above its listing (see
   * `onOpenFolder`) instead of requiring each file to be opened one at a time.
   */
  folderDatasetExtensions?: string[];
  /** Called when the folder-dataset banner is clicked, with the folder and its full listing. */
  onOpenFolder?: (folder: TcItem, items: TcItem[]) => void;
}

interface Crumb {
  id: string;
  name: string;
}

/**
 * Persistent (non-modal) folder browser embedded directly in the sidebar.
 *
 * Trimble Connect project files used to be reached through a picker dialog opened by a
 * "Connect…" button per dataset kind — two clicks and a kind decision before you could even
 * see the file list. This lists the current folder immediately and loads whatever you click,
 * one click, with the target layer decided by the file's own extension.
 */
export class ProjectTree {
  readonly root: HTMLElement;

  private readonly breadcrumbs: HTMLElement;
  private readonly list: HTMLElement;
  private readonly progress = progressBar();
  private trail: Crumb[];
  private cachedItems: TcItem[] | null = null;
  private generation = 0;

  constructor(
    private readonly api: CoreApi,
    private readonly options: ProjectTreeOptions,
  ) {
    this.trail = [{ id: options.rootId, name: options.rootName }];
    this.breadcrumbs = el("div", { class: "breadcrumbs" });
    this.list = el("div", { class: "tree" });
    this.root = el("div", { class: "project-tree" }, this.breadcrumbs, this.progress.root, this.list);
    void this.load(options.rootId);
  }

  /** Re-renders the current listing in place, e.g. after what's loaded changes. */
  refresh(): void {
    this.renderCrumbs();
    if (this.cachedItems) this.renderItems(this.cachedItems);
  }

  private async load(folderId: string): Promise<void> {
    const generation = ++this.generation;
    this.renderCrumbs();
    clear(this.list);

    this.progress.set(0.3);
    this.list.append(el("div", { class: "hint", text: t().loadingFolder }));
    try {
      const items = await this.api.listFolder(folderId);
      if (generation !== this.generation) return;
      this.cachedItems = items;
      this.progress.set(1);
      this.renderItems(items);
    } catch (error) {
      if (generation !== this.generation) return;
      this.cachedItems = null;
      clear(this.list);
      this.list.append(el("div", { class: "hint", text: t().folderListError(errorMessage(error)) }));
    } finally {
      setTimeout(() => this.progress.set(0), 250);
    }
  }

  private renderCrumbs(): void {
    clear(this.breadcrumbs);
    this.trail.forEach((crumb, index) => {
      if (index > 0) this.breadcrumbs.append(el("span", { text: "/" }));
      const button = el("button", { type: "button" }, crumb.name);
      button.addEventListener("click", () => {
        if (index === this.trail.length - 1) return;
        this.trail.length = index + 1;
        void this.load(crumb.id);
      });
      this.breadcrumbs.append(button);
    });
  }

  private renderItems(items: TcItem[]): void {
    clear(this.list);
    this.list.append(...this.folderDatasetBanner(items));
    const sorted = [...items].sort((a, b) => {
      if (isFolder(a) !== isFolder(b)) return isFolder(a) ? -1 : 1;
      return a.name.localeCompare(b.name, undefined, { numeric: true });
    });
    if (sorted.length === 0) {
      this.list.append(el("div", { class: "hint", text: t().folderEmpty }));
      return;
    }
    for (const item of sorted) this.list.append(this.renderItem(item));
  }

  /**
   * A folder holding two or more raster files (typically a DOM height model and an
   * orthophoto delivered side by side) gets a one-click offer to load the pair as a single
   * dataset, instead of requiring each file to be found and opened on its own.
   */
  private folderDatasetBanner(items: TcItem[]): HTMLElement[] {
    const { onOpenFolder, folderDatasetExtensions } = this.options;
    if (!onOpenFolder || !folderDatasetExtensions) return [];
    const candidates = items.filter((item) => !isFolder(item) && folderDatasetExtensions.includes(extOf(item.name)));
    if (candidates.length < 2) return [];

    const here = this.trail[this.trail.length - 1];
    const folder: TcItem = { id: here.id, name: here.name, type: "FOLDER" };
    const button = el(
      "button",
      { class: "primary", type: "button" },
      t().loadFolderPair(here.name, candidates.length),
    );
    button.addEventListener("click", () => onOpenFolder(folder, items));
    return [el("div", { class: "row" }, button)];
  }

  private renderItem(item: TcItem): HTMLElement {
    const folder = isFolder(item);
    const selectable = folder || this.options.accept.includes(extOf(item.name));
    const loaded = !folder && (this.options.isLoaded?.(item) ?? false);

    const row = el(
      "div",
      { class: "tree-item", ...(selectable ? {} : { "aria-disabled": "true" }) },
      el("span", { class: "glyph", text: folder ? "▸" : loaded ? "●" : "▪" }),
      el("span", { class: "name", text: item.name }),
      el("span", { class: "size", text: folder ? "" : formatBytes(item.size ?? 0) }),
    );
    if (loaded) row.dataset.selected = "true";

    row.addEventListener("click", () => {
      if (folder) {
        this.trail.push({ id: item.id, name: item.name });
        void this.load(item.id);
        return;
      }
      if (!selectable) return;
      this.options.onOpen(item, this.trail[this.trail.length - 1].id);
    });
    return row;
  }
}

/** Lower-case file extension, without the dot; empty string when the name has none. */
function extOf(name: string): string {
  return name.slice(name.lastIndexOf(".") + 1).toLowerCase();
}
